import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Moment from "moment";
import { useHistory, useLocation } from "react-router-dom";

import { SalesContext } from "../../context/salesContext";
import { CustomersContext } from "../../context/customersContext";
import SalesTable from "../tables/SalesTable";
import CustomerModal from "../modals/CustomerModal";
import SaleModal from "../modals/SaleModal";
import { DEFAULT_SALES_PAGE_SIZE, resolveApiLimit } from "../../constants/config";
import { SALE_STATUSES, getStatusChipTone, getStatusTone } from "../../constants/saleStatus";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { Badge, Card, EmptyState, PageHeader, SearchBar, Spinner } from "../ui";
import SalesStats from "./SalesStats";

const ALL_STATUSES = "all";
const ALL_BALANCES = "all";
const BALANCE_OVERDUE = "overdue";
const BALANCE_CLEARED = "cleared";

const Sales = () => {
    const customerAction = useContext(CustomersContext).action;
    const { customers } = useContext(CustomersContext);
    const { action, salesList, salesPagination, loading } = useContext(SalesContext);

    const location = useLocation();
    const history = useHistory();

    const [page, setPage] = useState(() => {
        const p = new URLSearchParams(location.search).get("page");
        return p !== null ? Math.max(0, parseInt(p, 10)) : 0;
    });
    const [rowsPerPage, setRowsPerPage] = useState(() => {
        const s = new URLSearchParams(location.search).get("size");
        return s !== null ? parseInt(s, 10) : DEFAULT_SALES_PAGE_SIZE;
    });
    const [search, setSearch] = useState(
        () => new URLSearchParams(location.search).get("q") || "",
    );
    const [statusFilter, setStatusFilter] = useState(
        () => new URLSearchParams(location.search).get("status") || ALL_STATUSES,
    );
    const [balanceFilter, setBalanceFilter] = useState(
        () => new URLSearchParams(location.search).get("balance") || ALL_BALANCES,
    );
    const [sortBy, setSortBy] = useState(
        () => new URLSearchParams(location.search).get("sortBy") || "orderDate",
    );
    const [sortOrder, setSortOrder] = useState(
        () => new URLSearchParams(location.search).get("sortOrder") || "desc",
    );
    const debouncedSearch = useDebouncedValue(search);

    useEffect(() => {
        const params = new URLSearchParams();
        if (page !== 0) params.set("page", page);
        if (rowsPerPage !== DEFAULT_SALES_PAGE_SIZE) params.set("size", rowsPerPage);
        if (sortBy !== "orderDate") params.set("sortBy", sortBy);
        if (sortOrder !== "desc") params.set("sortOrder", sortOrder);
        if (statusFilter !== ALL_STATUSES) params.set("status", statusFilter);
        if (balanceFilter !== ALL_BALANCES) params.set("balance", balanceFilter);
        if (search) params.set("q", search);
        history.replace({ search: params.toString() });
    }, [page, rowsPerPage, sortBy, sortOrder, statusFilter, balanceFilter, search, history]);

    const refreshSalesList = useCallback(
        () =>
            action.getSalesList({
                page: page + 1,
                limit: resolveApiLimit(rowsPerPage),
                search: debouncedSearch || undefined,
                status: statusFilter !== ALL_STATUSES ? [statusFilter] : undefined,
                balance: balanceFilter !== ALL_BALANCES ? balanceFilter : undefined,
                sortBy,
                sortOrder,
            }),
        [
            action,
            page,
            rowsPerPage,
            debouncedSearch,
            statusFilter,
            balanceFilter,
            sortBy,
            sortOrder,
        ],
    );

    useEffect(() => {
        refreshSalesList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, debouncedSearch, statusFilter, balanceFilter, sortBy, sortOrder]);

    const handleSortChange = useCallback((field, direction) => {
        setSortBy(field);
        setSortOrder(direction);
        setPage(0);
    }, []);

    const handleStatusFilterChange = (value) => {
        setStatusFilter(value);
        setPage(0);
    };

    const handleBalanceFilterChange = (value) => {
        setBalanceFilter(value);
        setPage(0);
    };

    const statusFilterConfig = useMemo(
        () => ({
            id: "status",
            label: "Filter by status",
            value: statusFilter,
            onChange: handleStatusFilterChange,
            options: [
                { value: ALL_STATUSES, label: "All", tone: "brand" },
                ...SALE_STATUSES.map((status) => ({
                    value: status,
                    label: status,
                    tone: getStatusChipTone(status),
                })),
            ],
        }),
        [statusFilter],
    );

    const balanceFilterConfig = useMemo(
        () => ({
            id: "balance",
            label: "Filter by balance",
            value: balanceFilter,
            onChange: handleBalanceFilterChange,
            options: [
                { value: ALL_BALANCES, label: "All", tone: "brand" },
                { value: BALANCE_OVERDUE, label: "Overdue", tone: "red" },
                { value: BALANCE_CLEARED, label: "Cleared", tone: "green" },
            ],
        }),
        [balanceFilter],
    );

    const filtersActive = statusFilter !== ALL_STATUSES || balanceFilter !== ALL_BALANCES;

    const [openCustomer, setOpenCustomer] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState();

    const [openSale, setOpenSale] = useState(false);
    const [selectedSale, setSelectedSale] = useState();

    const openCustomerModal = async (customerId) => {
        setOpenCustomer(true);
        const customer = await customerAction.getCustomer(customerId);
        setSelectedCustomer(customer);
    };

    const openSaleModal = async (saleId) => {
        setOpenSale(true);
        const sale = await action.getSale(saleId);
        setSelectedSale(sale);
    };

    const closeCustomerModal = () => {
        setOpenCustomer(false);
        setSelectedCustomer();
    };

    const closeSaleModal = () => {
        setOpenSale(false);
        setSelectedSale();
    };

    const initialLoad = !salesList;
    const list = salesList || [];

    const customerById = (id) => (customers || []).find((c) => c._id === id) || {};

    const renderPending = (sale) => {
        if (!sale.expectedCompletionDate) return null;
        const expected = Moment(sale.expectedCompletionDate).startOf("day");
        const today = Moment().startOf("day");
        const pending = expected.diff(today, "days");
        if (pending < 0) {
            return (
                <span className="text-xs font-semibold text-danger-600">
                    {Math.abs(pending)} day{Math.abs(pending) === 1 ? "" : "s"} overdue
                </span>
            );
        }
        if (pending === 0) {
            return <span className="text-xs font-semibold text-info-600">Due today</span>;
        }
        return (
            <span className="text-xs text-ink-500">
                {pending} day{pending === 1 ? "" : "s"} remaining
            </span>
        );
    };

    return (
        <div className="space-y-6">
            <PageHeader title="Sales" description="Browse, search, and manage every sale." />

            <SalesStats />

            <div className="md:hidden">
                <SearchBar
                    id="sales-search"
                    value={search}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setPage(0);
                    }}
                    placeholder="Search PO # or customer name / phone"
                />
            </div>

            {/* Mobile card list */}
            <div className="md:hidden">
                {initialLoad ? (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                ) : list.length === 0 ? (
                    <EmptyState
                        icon={<i className="fas fa-shopping-cart" />}
                        title="No sales found"
                        description="Try adjusting your search."
                    />
                ) : (
                    <ul className="space-y-3">
                        {list.map((sale) => {
                            const customer = customerById(sale.customerId);
                            const status = sale.workOrder && sale.workOrder.status;
                            return (
                                <li key={sale._id}>
                                    <button
                                        type="button"
                                        onClick={() => openSaleModal(sale._id)}
                                        className="block w-full text-left"
                                    >
                                        <Card className="hover:border-brand-200 hover:shadow-elevated">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-xs font-medium text-ink-500">
                                                        PO {sale.po || "—"}
                                                    </p>
                                                    <p className="font-semibold text-ink-900">
                                                        {customer.firstName} {customer.lastName}
                                                    </p>
                                                    {sale.product && (
                                                        <p className="text-xs text-ink-500">
                                                            {sale.product}
                                                        </p>
                                                    )}
                                                    {sale.expectedCompletionDate && (
                                                        <p className="mt-1 text-xs text-ink-500">
                                                            Expected{" "}
                                                            {Moment(
                                                                sale.expectedCompletionDate,
                                                            ).format("ll")}
                                                        </p>
                                                    )}
                                                    <div className="mt-1">
                                                        {renderPending(sale)}
                                                    </div>
                                                </div>
                                                <div className="flex flex-col items-end gap-1">
                                                    {status && (
                                                        <Badge tone={getStatusTone(status)}>
                                                            {status}
                                                        </Badge>
                                                    )}
                                                    {sale.salesPrice != null && (
                                                        <p className="text-sm font-semibold text-ink-900">
                                                            ${Number(sale.salesPrice).toFixed(2)}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        </Card>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {salesPagination && salesPagination.totalPages > 1 && (
                    <div className="mt-4 flex items-center justify-between text-sm">
                        <button
                            type="button"
                            className="text-brand-600 disabled:text-ink-300"
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            Previous
                        </button>
                        <span className="text-ink-500">
                            Page {page + 1} of {salesPagination.totalPages}
                        </span>
                        <button
                            type="button"
                            className="text-brand-600 disabled:text-ink-300"
                            onClick={() => setPage((p) => p + 1)}
                            disabled={page + 1 >= salesPagination.totalPages}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Desktop table */}
            <div className="hidden md:block">
                {initialLoad ? (
                    <div className="flex justify-center py-12">
                        <Spinner size="lg" />
                    </div>
                ) : (
                    <SalesTable
                        openCustomerModal={openCustomerModal}
                        openSaleModal={openSaleModal}
                        onRefresh={refreshSalesList}
                        salesList={list}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        totalCount={(salesPagination && salesPagination.totalSales) || 0}
                        onPageChange={setPage}
                        onRowsPerPageChange={(rows) => {
                            setRowsPerPage(rows);
                            setPage(0);
                        }}
                        loading={loading}
                        filters={[balanceFilterConfig, statusFilterConfig]}
                        search={search}
                        onSearchChange={(value) => {
                            setSearch(value);
                            setPage(0);
                        }}
                        onSortChange={handleSortChange}
                        defaultSort={{
                            id: { "workOrder.status": "status" }[sortBy] || sortBy,
                            direction: sortOrder,
                        }}
                        emptyMessage={
                            filtersActive
                                ? "No sales match the selected filters."
                                : "No sales to display."
                        }
                    />
                )}
            </div>

            <CustomerModal
                selectedCustomer={selectedCustomer}
                open={openCustomer}
                setOpen={setOpenCustomer}
                handleClose={closeCustomerModal}
            />

            <SaleModal
                selectedSale={selectedSale}
                setSelectedSale={setSelectedSale}
                open={openSale}
                setOpen={setOpenSale}
                handleClose={closeSaleModal}
                openSaleModal={openSaleModal}
            />
        </div>
    );
};

export default Sales;
